import { AppState } from '@/types';
import { initialState } from '@/context/initialState';
import { getCurrentTier } from '@/lib/tierSystem';

export function transformActivitiesFromSupabase(activities: any[], profiles: any[], userProfile?: any) {
  console.log('🔄 Transforming activities:', activities?.length || 0, 'raw activities');
  
  if (!activities || !Array.isArray(activities)) {
    console.error('❌ Invalid activities data:', activities);
    return [];
  }

  const transformed = activities.map((activity, index) => {
    try {
      // Add safety check for activity object
      if (!activity || typeof activity !== 'object') {
        console.error('❌ Invalid activity object at index', index, ':', activity);
        return null;
      }

      // Add safety checks for required properties
      if (!activity.id || !activity.name || activity.points === undefined) {
        console.error('❌ Activity missing required properties at index', index, ':', {
          id: activity.id,
          name: activity.name,
          points: activity.points,
          activity
        });
        return null;
      }

      return {
        ...activity,
        id: activity.id,
        name: activity.name,
        points: activity.points,
        timestamp: new Date(activity.timestamp),
        userId: activity.user_id,
        userName: userProfile?.preferred_name?.trim() || userProfile?.name || activity.user_id, // Use display name from userProfile
        likes: activity.likes || [],
        description: activity.description,
        duration: activity.duration,
        notes: activity.notes,
        photoUrl: activity.photo_url,
        policyName: activity.policy_name,
        annualizedPremium: activity.annualized_premium,
        comments: activity.activity_comments?.map(comment => ({
          id: comment.id,
          userId: comment.user_id,
          userName: userProfile?.preferred_name?.trim() || userProfile?.name || comment.user_id, // Use display name from userProfile
          content: comment.content,
          timestamp: new Date(comment.timestamp)
        })) || []
      };
    } catch (error) {
      console.error('❌ Error transforming activity at index', index, ':', error, activity);
      return null;
    }
  }).filter(Boolean); // Remove any null entries

  console.log('✅ Successfully transformed', transformed.length, 'activities');
  return transformed;
}

export function transformPetFromSupabase(pet: any) {
  if (!pet) return null;
  
  return {
    id: pet.id,
    name: pet.name,
    species: pet.species,
    level: pet.level || 1,
    experience: pet.experience || 0,
    stage: pet.stage as 'baby' | 'child' | 'teen' | 'adult' | 'elder',
    happiness: pet.happiness,
    hunger: pet.hunger,
    energy: pet.energy,
    health: pet.health,
    cleanliness: pet.cleanliness || 100,
    lastFed: new Date(pet.last_fed),
    lastPlayed: new Date(pet.last_played),
    lastCleaned: new Date(pet.last_cleaned || pet.last_played),
    lastChecked: new Date(pet.last_checked),
    createdAt: new Date(pet.created_at),
    availablePortions: pet.available_portions,
    portionTimestamps: pet.portion_timestamps?.map(ts => new Date(ts)) || [],
    unlockedItems: pet.unlocked_items as { food: string[]; toys: string[]; clothes: string[] },
    currentOutfit: pet.current_outfit as { hat?: string; accessory?: string; background: string | undefined },
    evolutionPoints: pet.evolution_points,
    personalityTraits: pet.personality_traits || [],
    socialStats: pet.social_stats as any,
    receivedGifts: [],
    ownerId: pet.user_id,
    imageUrl: pet.image_url || '',
    isFavorite: pet.is_favorite || false,
    evolutionStage: pet.evolution_stage || 0,
    type: pet.type || 'pet',
    food_fed: pet.food_fed || 0,
    food_needed: pet.food_needed || 50,
    obtained: pet.obtained || true,
    baseStats: pet.base_stats || {
      maxHunger: 100,
      maxHappiness: 100,
      decayRate: 1.0
    },
    personality: pet.personality || {
      bravery: 50,
      curiosity: 50,
      caring: 50
    },
    skills: pet.skills || {
      endurance: 10,
      agility: 10,
      intelligence: 10
    },
    clothing: pet.clothing
  };
}

export function transformChallengesFromSupabase(challenges: any[]) {
  return challenges?.map(challenge => ({
    ...challenge,
    id: challenge.id,
    title: challenge.title,
    description: challenge.description,
    type: challenge.type as 'daily_streak' | 'point_target' | 'activity_count' | 'pet_care' | 'custom',
    target: challenge.target,
    progress: challenge.progress,
    startDate: new Date(challenge.start_date),
    endDate: new Date(challenge.end_date),
    status: challenge.status as 'active' | 'completed' | 'expired' | 'available',
    difficulty: challenge.difficulty as 'easy' | 'medium' | 'hard',
    category: challenge.category,
    isPublic: challenge.is_public,
    isCustom: challenge.is_custom,
    reward: {
      points: challenge.reward_points,
      petItems: challenge.reward_items,
      badges: challenge.reward_badges,
      title: challenge.reward_title
    },
    participantCount: challenge.participants?.length || 0,
    participants: challenge.participants || [],
    participantProgress: challenge.participant_progress as Record<string, number>,
    invitations: [],
    creatorId: challenge.user_id,
    creatorName: challenge.user_id,
    maxParticipants: challenge.max_participants,
    requiresProof: challenge.requires_proof,
    customRules: challenge.custom_rules
  })) || [];
}

function transformSimplePetFromSupabase(simplePet: any) {
  if (!simplePet) return null;
  
  return {
    id: simplePet.id,
    name: simplePet.name,
    emoji: simplePet.emoji,
    stage: simplePet.stage as 'baby' | 'child' | 'teen' | 'adult' | 'elder',
    happiness: simplePet.happiness,
    health: simplePet.health,
    totalPoints: simplePet.total_points,
    lastInteraction: new Date(simplePet.last_interaction),
    createdAt: new Date(simplePet.created_at),
    portionTimestamps: (simplePet.portion_timestamps || []).map((ts: any) => new Date(ts)),
    pledgeSheetId: simplePet.pledge_sheet_id,
    dailyTargetProgress: simplePet.daily_target_progress,
    weeklyPledgeProgress: simplePet.weekly_pledge_progress,
    lastDailyUpdate: new Date(simplePet.last_daily_update),
    lastWeeklyUpdate: new Date(simplePet.last_weekly_update),
    pledgePerformanceStreak: simplePet.pledge_performance_streak,
    hat: simplePet.hat,
    accessory: simplePet.accessory,
    // Food & Economy System
    activityCredits: simplePet.treat_tokens || 10,
    hunger: simplePet.hunger || 80,
    energy: simplePet.energy || 80,
  };
}

export function transformAppStateFromSupabase(
  profile: any,
  stats: any,
  activities: any[],
  pet: any,
  challenges: any[],
  userProfile?: any,
  habits?: any[],
  simplePet?: any
): AppState {
  const transformedActivities = transformActivitiesFromSupabase(activities, [], userProfile);
  const transformedPet = transformPetFromSupabase(pet);
  const transformedSimplePet = transformSimplePetFromSupabase(simplePet);
  const transformedChallenges = transformChallengesFromSupabase(challenges);

  // Calculate actual total points from activities with safety check
  const actualTotalPoints = transformedActivities.reduce((total, activity) => {
    if (!activity || typeof activity.points !== 'number') {
      console.error('❌ Invalid activity in reduce operation:', activity);
      return total;
    }
    return total + activity.points;
  }, 0);
  
  console.log(`📊 Points calculation: ${transformedActivities.length} activities, ${actualTotalPoints} total points`);
  console.log(`💾 Database stats: total_points=${stats?.total_points}, current_week_points=${stats?.current_week_points}`);
  
  // Use the HIGHER value between calculated and stored to prevent loss
  const finalTotalPoints = Math.max(actualTotalPoints, stats?.total_points || 0);
  console.log(`🎯 Final total points: ${finalTotalPoints}`);
  
  // Calculate current week points (activities from this week)
  const weekStart = stats?.weekly_start_date ? new Date(stats.weekly_start_date) : new Date();
  const currentWeekStart = new Date();
  currentWeekStart.setDate(currentWeekStart.getDate() - currentWeekStart.getDay()); // Start of current week
  currentWeekStart.setHours(0, 0, 0, 0);
  
  // Check if we're in a new week compared to the stored week start
  const storedWeekTime = weekStart.getTime();
  const currentWeekTime = currentWeekStart.getTime();
  const daysDifference = Math.floor((currentWeekTime - storedWeekTime) / (1000 * 60 * 60 * 24));
  const isNewWeek = daysDifference >= 7;
  
  const currentWeekActivities = transformedActivities.filter(activity => 
    activity.timestamp >= weekStart
  );
  const actualCurrentWeekPoints = currentWeekActivities.reduce((total, activity) => {
    if (!activity || typeof activity.points !== 'number') {
      console.error('❌ Invalid activity in week points reduce operation:', activity);
      return total;
    }
    return total + activity.points;
  }, 0);
  
  // Force reset if we detect a new week
  const shouldForceReset = isNewWeek;
  // Always trust the database value for current week points to prevent doubling after undo
  const finalCurrentWeekPoints = shouldForceReset ? 0 : (stats?.current_week_points || 0);
  
  // Calculate activities logged today from actual data using Singapore timezone (consistent with daily reset)
  const now = new Date();
  const singaporeOffset = 8 * 60; // Singapore is UTC+8 (minutes)
  const nowInSingapore = new Date(now.getTime() + (singaporeOffset - now.getTimezoneOffset()) * 60 * 1000);
  
  // Get Singapore midnight for today
  const todayMidnightSingapore = new Date(
    nowInSingapore.getFullYear(), 
    nowInSingapore.getMonth(), 
    nowInSingapore.getDate()
  );
  
  const todayActivities = transformedActivities.filter(activity => {
    const activityDate = new Date(activity.timestamp);
    const activitySingaporeTime = new Date(activityDate.getTime() + (singaporeOffset - activityDate.getTimezoneOffset()) * 60 * 1000);
    const activityMidnightSingapore = new Date(
      activitySingaporeTime.getFullYear(), 
      activitySingaporeTime.getMonth(), 
      activitySingaporeTime.getDate()
    );
    return activityMidnightSingapore.getTime() === todayMidnightSingapore.getTime();
  });
  const actualActivitiesLoggedToday = todayActivities.length;
  
  console.log(`📅 TODAY ACTIVITIES DEBUG:`, {
    todayMidnightSingapore: todayMidnightSingapore.toISOString(),
    singaporeTime: nowInSingapore.toISOString(),
    totalActivities: transformedActivities.length,
    todayActivities: todayActivities.length,
    firstFewActivities: transformedActivities.slice(0, 3).map(a => ({
      name: a.name,
      timestamp: a.timestamp.toISOString(),
      activityMidnight: new Date(a.timestamp.getFullYear(), a.timestamp.getMonth(), a.timestamp.getDate()).getTime(),
      todayMidnight: todayMidnightSingapore.getTime()
    })),
    dbActivitiesLoggedToday: stats?.activities_logged_today
  });
  
  console.log(`📅 Week calculation: ${currentWeekActivities.length} activities this week, calculated: ${actualCurrentWeekPoints}, db: ${stats?.current_week_points}, isNewWeek: ${isNewWeek}, shouldForceReset: ${shouldForceReset}, final: ${finalCurrentWeekPoints}`);
  console.log(`📅 Today calculation: ${actualActivitiesLoggedToday} activities logged today (calculated from ${transformedActivities.length} total activities)`);
  console.log(`🎯 STREAK LOAD DEBUG: DB streak=${stats?.streak}, DB lastStreakDate=${stats?.last_streak_date}`);
  console.log(`🔄 PERSISTENCE DEBUG: lastDailyReset=${stats?.last_daily_reset}, weeklyStartDate=${stats?.weekly_start_date}`);

  // CRITICAL: Always use database value for activity credits to prevent reset after spending
  // Only calculate from points if database value is null/undefined (new user)
  const calculatedActivityCredits = finalTotalPoints * 0.1;
  const dbActivityCredits = stats?.activity_credits;
  
  // Use database value if it exists (even if 0), otherwise calculate from points
  const finalActivityCredits = dbActivityCredits !== null && dbActivityCredits !== undefined 
    ? dbActivityCredits 
    : calculatedActivityCredits;
  
  console.log(`💰 Activity Credits calculation:`, {
    totalPoints: finalTotalPoints,
    dbActivityCredits,
    calculatedActivityCredits,
    finalActivityCredits,
    willUseDbValue: dbActivityCredits !== null && dbActivityCredits !== undefined
  });

  return {
    ...initialState,
    user: profile ? {
      id: profile.user_id,
      name: profile.name || 'Unknown User',
      preferredName: profile.preferred_name || null,
      email: profile.email,
      weeklyGoal: profile.weekly_goal,
      profilePicture: profile.profile_picture,
      role: profile.role || 'user'
    } : null,
    isAuthenticated: true,
    totalPoints: finalTotalPoints, // Use final calculated total
    experience: Math.max(finalTotalPoints, stats?.experience || 0), // XP = totalPoints (cumulative, never decreases)
    activityCredits: finalActivityCredits,
    currentWeekPoints: finalCurrentWeekPoints, // Use final calculated week points
    streak: (() => {
      // Validate streak on load — reset if last activity was more than 1 day ago
      const dbStreak = stats?.streak || 0;
      const dbLastStreakDate = stats?.last_streak_date || null;
      if (!dbLastStreakDate || dbStreak === 0) return 0;
      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const wasToday = dbLastStreakDate === now.toDateString();
      const wasYesterday = dbLastStreakDate === yesterday.toDateString();
      return (wasToday || wasYesterday) ? dbStreak : 0;
    })(),
    lastStreakDate: stats?.last_streak_date || null,
    activitiesLoggedToday: actualActivitiesLoggedToday, // Always use calculated value from actual activities
    activityCounts: stats?.activity_counts as Record<string, number> || initialState.activityCounts,
    badges: (stats?.badges || []).map(badge => {
      // Handle both string format (old badge names) and JSON string format (new badge objects)
      if (typeof badge === 'string') {
        try {
          // Try parsing as JSON first (new format)
          const parsed = JSON.parse(badge);
          return {
            id: parsed.id,
            name: parsed.name,
            description: parsed.description || '',
            icon: parsed.icon || '',
            rarity: parsed.rarity || 'common' as const,
            unlockedAt: parsed.unlockedAt ? new Date(parsed.unlockedAt) : new Date()
          };
        } catch {
          // If parsing fails, it's an old badge name string
          return {
            id: badge,
            name: badge,
            description: '',
            icon: '',
            rarity: 'common' as const,
            unlockedAt: new Date()
          };
        }
      }
      // Fallback for any other format
      return {
        id: badge.id || '',
        name: badge.name || '',
        description: badge.description || '',
        icon: badge.icon || '',
        rarity: badge.rarity || 'common' as const,
        unlockedAt: badge.unlockedAt ? new Date(badge.unlockedAt) : new Date()
      };
    }),
    weeklyStartDate: shouldForceReset ? currentWeekStart : (stats?.weekly_start_date ? new Date(stats.weekly_start_date) : initialState.weeklyStartDate),
    weeklyGoalStreak: stats?.weekly_goal_streak || 0,
    lastWeekGoalAchieved: stats?.last_week_goal_achieved || false,
    weeklyBonusEarned: stats?.weekly_bonus_earned || 0,
    weeklyBonusAwarded: stats?.weekly_bonus_awarded || false,
    lastLoginDate: stats?.last_login_date ? new Date(stats.last_login_date) : null,
    lastDailyReset: stats?.last_daily_reset || null,
    activities: transformedActivities,
    virtualPet: transformedPet,
    simplePet: transformedSimplePet,
    challenges: transformedChallenges,
    activeChallenges: transformedChallenges.filter(c => c.status === 'active'),
    // Keep other fields with initial values
    currentTier: getCurrentTier(finalTotalPoints),
    leaderboardPosition: initialState.leaderboardPosition,
    petInteractions: [],
    teamPets: [],
    challengeFriends: initialState.challengeFriends,
    challengeInvitations: initialState.challengeInvitations,
    weeklyGoal: profile?.weekly_goal || initialState.weeklyGoal,
    habits: habits || []
  };
}
